import cx from 'classnames'
import { find, isEqual } from 'lodash'
import { useSelector } from 'react-redux'

import { Favorite } from 'api/clients/FavoriteClient'
import { ReactComponent as FilledStarIcon } from 'assets/icons/filledStar.svg'
import { ReactComponent as StarIcon } from 'assets/icons/star16.svg'
import { FileType, RootState } from 'types'
import { Tooltip } from 'components/Tooltip/Tooltip'
import Styles from './FavoriteButton.module.scss'
import { IconButton } from 'components/IconButton/IconButton'
import { ButtonSize } from 'components/Button/Types'
import { Placement } from '@floating-ui/core'
import { useCreateFavorite } from 'hooks/queries/favorites/useCreateFavorite'
import { useDeleteFavorite } from 'hooks/queries/favorites/useDeleteFavorite'

interface Props {
  className?: string
  favoritableId: Favorite['favoritable_id']
  favoritableType: FileType
  favoritableTitle: string
  size?: ButtonSize
  tooltipPlacement?: Placement
}
export const FavoriteButton = ({
  className,
  favoritableId,
  favoritableType,
  favoritableTitle,
  size = 'sm',
  tooltipPlacement = 'bottom',
}: Props) => {
  const favorite = useSelector(
    ({ favorites }: RootState) =>
      find(favorites, {
        favoritable_id: favoritableId,
      }) as Favorite | undefined,
    isEqual,
  )
  const { mutate: createFavorite } = useCreateFavorite()
  const { mutate: deleteFavorite } = useDeleteFavorite()

  const handleFavorite = () => {
    if (favorite) {
      deleteFavorite({ id: favorite.id, title: favorite.favoritable_title })
    } else {
      createFavorite({
        favoritableId,
        favoritableType,
        favoritableTitle,
      })
    }
  }

  return (
    <Tooltip label={favorite ? 'Unfavorite' : 'Favorite'} placement={tooltipPlacement}>
      <span className={className}>
        <IconButton
          size={size}
          onPress={handleFavorite}
          data-testid="favoriteButton"
          aria-label={favorite ? 'Remove from favorites' : 'Save to favorites'}
        >
          {favorite ? (
            <FilledStarIcon className={cx(Styles.favoriteButton, Styles.active)} aria-hidden="true" />
          ) : (
            <StarIcon className={Styles.favoriteButton} aria-hidden="true" />
          )}
        </IconButton>
      </span>
    </Tooltip>
  )
}
